/* eslint-disable react-refresh/only-export-components */
import { useMemo } from 'react';
import { Card, CardContent, Typography, Box } from '@mui/material';
import { withJsonFormsLayoutProps } from '@jsonforms/react';
import {
  type LayoutProps,
  type UISchemaElement,
  rankWith,
  uiTypeIs,
  type JsonFormsRendererRegistryEntry,
} from '@jsonforms/core';
import { JsonFormsDispatch } from '@jsonforms/react';

/**
 * Custom Group renderer for JSONForms with reduced padding.
 * Provides a card layout for grouping form elements with minimal padding.
 */
const GroupLayoutRendererComponent = ({
  visible,
  enabled,
  uischema,
  schema,
  path,
  renderers,
  cells,
}: LayoutProps) => {
  const groupUiSchema = uischema as UISchemaElement & {
    label?: string;
    elements?: UISchemaElement[];
  };

  const elements = useMemo(() => groupUiSchema.elements || [], [groupUiSchema.elements]);

  if (!visible) {
    return null;
  }

  const label = groupUiSchema.label;

  return (
    <Card
      sx={{
        mb: 2,
        '& .MuiCardContent-root': {
          padding: '12px 16px', // Reduced from default 16px 24px
          '&:last-child': {
            paddingBottom: '12px', // Reduced from default 24px
          },
        },
      }}
    >
      <CardContent>
        {label && (
          <Typography variant="subtitle1" component="h3" sx={{ mb: 1, fontWeight: 500 }}>
            {label}
          </Typography>
        )}
        <Box>
          {elements.map((element, index) => (
            <JsonFormsDispatch
              key={`${path}-${index}`}
              uischema={element}
              schema={schema}
              path={path}
              enabled={enabled}
              renderers={renderers}
              cells={cells}
            />
          ))}
        </Box>
      </CardContent>
    </Card>
  );
};

const GroupLayoutRenderer = withJsonFormsLayoutProps(GroupLayoutRendererComponent);

/**
 * Tester for the Group renderer.
 * Activates for all Group UI schema elements.
 *
 * RENDERER RANKING STRATEGY:
 * JSONForms uses a ranking system to determine which renderer to use.
 * Higher ranks take priority over lower ranks.
 *
 * Current Ranking Hierarchy:
 * - Rank 1-3:   Core JSONForms default renderers (layouts, controls)
 * - Rank 4:     Custom control renderers (TextAreaRenderer)
 * - Rank 5:     Custom layout renderers (this GroupLayoutRenderer, EntitySelectRenderer)
 * - Rank 10+:   Reserved for future highly-specific renderers
 *
 * This renderer uses rank 5 to override the default Group renderer (rank 1)
 * to provide custom Material-UI Card styling with reduced padding.
 *
 * CAUTION: This renderer applies to ALL Group elements in the application.
 * Changes to styling here will affect every form using Group layouts.
 */
export const groupLayoutRendererTester = rankWith(
  5, // Higher priority than default Group renderer (which is 1)
  uiTypeIs('Group'),
);

export const groupLayoutRendererEntry: JsonFormsRendererRegistryEntry = {
  tester: groupLayoutRendererTester,
  renderer: GroupLayoutRenderer,
};
